/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import type {
  ForwardRefExoticComponent,
  JSX,
  KeyboardEventHandler,
  ReactElement,
  RefAttributes,
  RefObject,
} from "react";
import { createRef, forwardRef, useMemo, useRef, useState } from "react";
import type { Control } from "react-hook-form";
import { useController } from "react-hook-form";

import { cssMediaQueryIsSafari } from "../../../../../emotion";
import type { ThemeType } from "../../../../../global/emotion";
import {
  colors,
  fontSizes,
  lineHeights,
  numericPercentages,
  numericPixels,
  radius,
  spacing,
  stroke,
  useTheme,
} from "../../../../../global/emotion";
import type { HallowElement } from "../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../types/hallowElementProps.types";
import type { ButtonProps } from "../../../_base/_core/Button/Button.emotion";
import { Button } from "../../../_base/_core/Button/Button.emotion";
import { Tag } from "../../../_base/_core/Tag/Tag.emotion";
import { Text } from "../../../_base/_core/Text/Text.emotion";
import type { IconElement } from "../../../_base/icons/Icon/Icon";

const hallowElement: HallowElement = "input";

const safariEmptyDateInputStyle: Interpolation = {
  [cssMediaQueryIsSafari]: {
    color: "transparent",
  },
};
const inputStyle: Interpolation<ThemeType> = (theme) => ({
  "-moz-appearance": "textfield",
  appearance: "none",
  backgroundColor: colors.transparent,
  borderColor: theme.semanticColors.neutralsLowest,
  borderRadius: radius.ms,
  borderStyle: "solid",
  borderWidth: stroke.regular,
  color: theme.semanticColors.primary,
  fontSize: fontSizes.bodyM,
  height: numericPixels[40],
  lineHeight: lineHeights.bodyM,
  minHeight: numericPixels[64],
  outline: "none",
  padding: `${spacing.ms} ${spacing.m}`,
  transition: "border 200ms ease-in",
  width: numericPercentages[100],
  ":hover:not(:disabled):not(:focus)": {
    borderColor: theme.semanticColors.neutralsVeryLow,
  },
  ":disabled": {
    color: theme.semanticColors.neutralsMedHigh,
    cursor: "not-allowed",
  },
  ":focus": {
    borderColor: theme.semanticColors.primary,
    outline: "none",
  },
  "::-webkit-outer-spin-button, ::-webkit-inner-spin-button": {
    appearance: "none",
    margin: 0,
  },
});
const inputDarkStyle: Interpolation = {
  "::-webkit-calendar-picker-indicator": {
    filter: "invert(1)",
  },
};
const inputIsFocusedIsPersistentLabelStyle: Interpolation = {
  paddingTop: numericPixels[28],
};
const inputWithSymbolStyle: Interpolation = {
  paddingLeft: `calc(${spacing.ms} + ${spacing.m})`,
};
const inputWithIconStyle: Interpolation = {
  paddingLeft: `calc(${spacing.ms} + ${spacing.xxl})`,
};
const inputWithIconAndSymbolStyle: Interpolation = {
  paddingLeft: `calc(${spacing.ms} + ${spacing.xxl} + ${spacing.m})`,
};
const labelStyle: Interpolation = {
  display: "block",
  opacity: 1,
  position: "relative",
  transition: "opacity 250ms",
};
const labelIsHiddenStyle: Interpolation = {
  opacity: 0,
};
const labelWithSymbolStyle: Interpolation = {
  paddingLeft: spacing.ms,
};
const labelWithIconStyle: Interpolation = {
  paddingLeft: spacing.xl,
};
const labelWithIconAndSymbolStyle: Interpolation = {
  paddingLeft: spacing.xxl,
};
const placeholderStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsHigh,
  left: numericPixels[18],
  pointerEvents: "none",
  position: "absolute",
  top: numericPixels[22],
  transformOrigin: "0% 50%",
  transition: "all 250ms",
});
const placeHolderOverflowEllipsisStyle: Interpolation = {
  maxWidth: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const populatedStyle: Interpolation = {
  top: numericPixels[10],
  transform: "scale(.75)",
};
const symbolStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsMedium,
  left: spacing.m,
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
});
const iconStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsMedium,
  left: spacing.m,
  position: "absolute",
  top: numericPercentages[50],
  transform: "translateY(-50%)",
});
const tagStyle: Interpolation = {
  position: "absolute",
  right: spacing.m,
  top: "50%",
  transform: "translateY(-50%)",
};
const withCTAStyle: Interpolation = {
  borderRadius: radius.full,
};
const ctaStyle: Interpolation = {
  position: "absolute",
  right: spacing.ms,
  top: numericPercentages[50],
  transform: "translateY(-50%)",
};

export type TextInputProps = HallowElementProps<typeof hallowElement> & {
  isPersistentLabel?: boolean;
  symbol?: string;
  tag?: string;
  startIcon?: IconElement;
  placeholderCSS?: Interpolation;
  labelRef?: RefObject<HTMLLabelElement | null>;
  button?: ReactElement<ButtonProps>;
};

const InternalTextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      isPersistentLabel = true,
      name,
      value,
      placeholder,
      symbol,
      tag,
      type,
      placeholderCSS = {},
      startIcon = null,
      labelRef = createRef<HTMLLabelElement>(),
      button = null,
      className = "",
      ...props
    },
    ref,
  ) => {
    const [focused, setFocused] = useState<boolean>(false);
    const { renderedTheme } = useTheme();
    return (
      <label
        onBlur={() => setFocused(false)}
        onFocus={() => setFocused(true)}
        css={labelStyle}
        className={className}
        ref={labelRef}
      >
        {startIcon && <div css={iconStyle}>{startIcon}</div>}
        {symbol && (
          <Text size="m" css={symbolStyle} type="body">
            {symbol}
          </Text>
        )}
        {placeholder && (
          <Text
            size="m"
            type="body"
            css={[
              placeholderStyle,
              type === "date" && [
                populatedStyle,
                placeHolderOverflowEllipsisStyle,
              ],
              isPersistentLabel
                ? (focused || !!value) && [
                    populatedStyle,
                    placeHolderOverflowEllipsisStyle,
                  ]
                : (focused || !!value) && labelIsHiddenStyle,
              symbol && labelWithSymbolStyle,
              startIcon && labelWithIconStyle,
              startIcon && symbol && labelWithIconAndSymbolStyle,
              type === "date" &&
                (value === "" || value === null) &&
                safariEmptyDateInputStyle,
              placeholderCSS,
            ]}
          >
            {placeholder}
          </Text>
        )}
        {tag && (
          <Tag variant="neutral" css={tagStyle}>
            {tag}
          </Tag>
        )}
        <input
          name={name}
          css={[
            inputStyle,
            renderedTheme === "dark" && inputDarkStyle,
            placeholder &&
              type === "date" &&
              inputIsFocusedIsPersistentLabelStyle,
            (focused || !!value) &&
              isPersistentLabel &&
              placeholder &&
              inputIsFocusedIsPersistentLabelStyle,
            symbol && inputWithSymbolStyle,
            startIcon && inputWithIconStyle,
            startIcon && symbol && inputWithIconAndSymbolStyle,
          ]}
          className={className}
          {...props}
          type={type}
          value={value}
          ref={ref}
        />
        {button && (
          <div key={"button"} css={ctaStyle}>
            {button}
          </div>
        )}
      </label>
    );
  },
) as ForwardRefExoticComponent<
  Omit<TextInputProps, "ref"> & RefAttributes<HTMLInputElement>
>;

export const TextInput = forwardRef((props, ref) => (
  <InternalTextInput ref={ref} {...props} />
)) as ForwardRefExoticComponent<
  Omit<TextInputProps, "ref" | "button"> & RefAttributes<HTMLInputElement>
> & {
  Field: (props: TextInputFieldProps) => JSX.Element;
  WithCTA: (props: TextInputWithCTAProps) => JSX.Element;
};

type TextInputFieldProps = TextInputProps & {
  control: Control<any, any>;
};

TextInput.Field = ({
  control,
  name,
  required,
  ...props
}: TextInputFieldProps) => {
  const {
    field: { ref, ...field },
  } = useController({
    name,
    control,
    rules: { required },
  });

  return (
    <InternalTextInput
      {...field}
      {...props}
      value={field.value}
      ref={ref}
      onChange={(event) => field.onChange?.(event?.target.value)}
    />
  );
};

type TextInputWithCTAProps = Omit<TextInputProps, "button"> & {
  buttonText: string;
  onButtonClick: (inputValue: string) => void;
};

TextInput.WithCTA = ({
  buttonText,
  onButtonClick,
  disabled,
  readOnly,
  value,
  onInput,
  onKeyDownCapture,
  ...props
}: TextInputWithCTAProps) => {
  const ref = useRef<HTMLInputElement>(null);
  const [localValue, setLocalValue] = useState<string>("");

  const imputedValue = useMemo(() => {
    return value ?? localValue;
  }, [value, localValue]);
  const imputedOnInput = useMemo(() => {
    if (onInput) {
      return onInput;
    }
    return (e) => setLocalValue(e.currentTarget.value);
  }, [onInput]);

  const handleSubmit = () => {
    ref.current?.reportValidity();
    if (!ref.current?.validity?.valid) return;
    onButtonClick(ref.current.value ?? "");
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === "Enter" || e.key === "Return") {
      e.preventDefault();
      e.stopPropagation();
      handleSubmit();
      return;
    }

    return onKeyDownCapture?.(e);
  };

  return (
    <InternalTextInput
      ref={ref}
      button={
        <Button
          variant={"neutral"}
          size={"s"}
          onClick={handleSubmit}
          disabled={disabled || readOnly}
        >
          {buttonText}
        </Button>
      }
      disabled={disabled}
      readOnly={readOnly}
      css={withCTAStyle}
      value={imputedValue}
      onInput={imputedOnInput}
      onKeyDownCapture={handleKeyDown}
      {...props}
    />
  );
};

InternalTextInput.displayName = "InternalTextInput";
TextInput.displayName = "TextInput";
